/**
 * Destek modalı ekran görüntüsü için saf (DOM'suz, test edilebilir) yardımcılar.
 *
 * #727: eskiden `html2canvas(document.body, { scale: 0.5 })` +
 * `toDataURL('image/jpeg', 0.7)` kullanılıyordu — 1837x996 ekran 919x649'a
 * iniyor ve yazılar okunamıyordu. Bu modül üç saf hesabı taşır:
 *  - `resolveCaptureScale`: cihaz piksel oranına göre yakalama ölçeği (1-2 arası).
 *  - `resolveLongEdgeDimensions`: uzun kenar tavanını aşan görüntüyü oranı
 *    koruyarak küçültür.
 *  - `encodeWithQualityCeiling`: sonuç data URL boyutu bir tavanı aşarsa
 *    kaliteyi kademeli düşürür (asla 0.5 ölçeğe dönmez, yalnız JPEG kalitesi
 *    düşer).
 *
 * Gerçek `modern-screenshot`/`HTMLCanvasElement` çağrıları bu dosyanın dışında
 * (support-modal-provider.tsx) kalır; burada yalnız hesap mantığı test edilir.
 */

/** Görünür alanı en az `devicePixelRatio` netliğinde, en fazla 2x yakala. */
export const SCREENSHOT_JPEG_QUALITY = 0.9
export const SCREENSHOT_MAX_LONG_EDGE = 2560
/** ~2.5 MB base64 data URL tavanı (rapor/e-posta/GitHub gövdesi bütçeleri için makul). */
export const SCREENSHOT_DATA_URL_BYTE_CEILING = 2_500_000
export const SCREENSHOT_QUALITY_FALLBACKS = [0.8, 0.7] as const

/**
 * Yakalama ölçeğini `devicePixelRatio`'dan türetir; 1 ile 2 arasında sınırlar.
 * `devicePixelRatio` tanımsız/geçersizse 1 varsayılır.
 */
export function resolveCaptureScale(devicePixelRatio: number | undefined | null): number {
  const ratio = typeof devicePixelRatio === 'number' && Number.isFinite(devicePixelRatio)
    ? devicePixelRatio
    : 1
  return Math.min(Math.max(ratio, 1), 2)
}

export type CaptureDimensions = { width: number; height: number }
export type ResizedDimensions = CaptureDimensions & { scaled: boolean }

/**
 * Uzun kenar `maxLongEdge`'i aşıyorsa oranı koruyarak küçültülmüş boyutu döner.
 * Kaynak boyutları geçersiz/eksikse (ör. test double'larında `width`/`height`
 * yoksa) küçültme uygulanmaz — çağıran orijinal canvas'ı kullanmaya devam eder.
 */
export function resolveLongEdgeDimensions(
  source: CaptureDimensions,
  maxLongEdge: number = SCREENSHOT_MAX_LONG_EDGE,
): ResizedDimensions {
  const width = Number.isFinite(source.width) && source.width > 0 ? source.width : 0
  const height = Number.isFinite(source.height) && source.height > 0 ? source.height : 0
  const longEdge = Math.max(width, height)

  if (longEdge <= 0 || longEdge <= maxLongEdge) {
    return { width: source.width, height: source.height, scaled: false }
  }

  const ratio = maxLongEdge / longEdge
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
    scaled: true,
  }
}

/**
 * #1420: kaydırılmış pencerede yakalanacak alanı kaydırma kadar yukarı/sola çeker.
 * Eskiden gövdeye `transform: translate(-x, -y)` veriliyordu; gövdenin kutusu
 * (`overflow-x: hidden` + viewport boyutuna sabitlenmiş yükseklik) birlikte kayıyor,
 * içerik kutu dışında kalıp kırpılıyordu: 300 px kaydırmada alt %30, viewport yüksekliği
 * kadar kaydırmada ekranın TAMAMI şeffaf kalıyor, JPEG şeffafı siyaha çeviriyordu.
 * Negatif kenar boşluğu içeriği aynı kadar kaydırır ama `fixed` öğeler (kenar çubuğu,
 * üst çubuk) yerinde kalır (dönüşümlü atada kutu değiştiği için kayıyorlardı);
 * `overflow: visible` kutu dışı içeriğin kırpılmasını engeller.
 * Kaydırma yoksa `undefined` döner (stil eklenmez).
 */
export function resolveScrollOffsetStyle(
  scrollX: number,
  scrollY: number,
): Record<string, string> | undefined {
  const x = Number.isFinite(scrollX) ? scrollX : 0
  const y = Number.isFinite(scrollY) ? scrollY : 0
  if (x === 0 && y === 0) return undefined
  return { marginLeft: `${-x}px`, marginTop: `${-y}px`, overflow: 'visible' }
}

const TRANSPARENT_COLOR = /^(transparent|rgba?\([^)]*[,/]\s*0(\.0+)?%?\s*\))$/i

/**
 * Yakalama tuvalinin zemin rengi: ilk saydam olmayan aday (gövde, sonra html), yoksa beyaz.
 * #1420: tuval şeffaf başlar ve JPEG şeffafı SİYAH yazar; zemin rengi verilmezse her
 * boş/kırpılmış bölge siyah çıkar.
 */
export function resolveCaptureBackground(candidates: ReadonlyArray<string | null | undefined>): string {
  for (const color of candidates) {
    const value = (color ?? '').trim()
    if (value && !TRANSPARENT_COLOR.test(value)) return value
  }
  return '#ffffff'
}

/** Bir data URL'in gövdesindeki (virgülden sonraki) base64 karakter sayısını döner. */
export function estimateDataUrlBytes(dataUrl: string): number {
  const commaIndex = dataUrl.indexOf(',')
  return commaIndex >= 0 ? dataUrl.length - commaIndex - 1 : dataUrl.length
}

/**
 * Kalite düşürme merdiveninde `currentQuality`'den sonraki basamağı döner.
 * `currentQuality` merdivende yoksa (ör. ilk çağrının başlangıç kalitesi)
 * merdivenin ilk basamağı döner; merdiven tükendiyse `null`.
 */
export function nextQualityStep(
  currentQuality: number,
  fallbacks: readonly number[] = SCREENSHOT_QUALITY_FALLBACKS,
): number | null {
  const index = fallbacks.indexOf(currentQuality)
  if (index === -1) return fallbacks[0] ?? null
  return fallbacks[index + 1] ?? null
}

export type EncodeWithQualityCeilingOptions = {
  initialQuality?: number
  byteCeiling?: number
  fallbacks?: readonly number[]
}

export type EncodeWithQualityCeilingResult = {
  dataUrl: string
  quality: number
}

/**
 * `encode(quality)` çağrılarını, sonuç data URL tavanı aşmayana veya kalite
 * merdiveni tükenene kadar tekrarlar. Asla ölçeği (scale) değiştirmez —
 * yalnız JPEG kalitesini kademeli düşürür (0.9 → 0.8 → 0.7).
 */
export function encodeWithQualityCeiling(
  encode: (quality: number) => string,
  options: EncodeWithQualityCeilingOptions = {},
): EncodeWithQualityCeilingResult {
  const byteCeiling = options.byteCeiling ?? SCREENSHOT_DATA_URL_BYTE_CEILING
  const fallbacks = options.fallbacks ?? SCREENSHOT_QUALITY_FALLBACKS

  let quality = options.initialQuality ?? SCREENSHOT_JPEG_QUALITY
  let dataUrl = encode(quality)

  while (estimateDataUrlBytes(dataUrl) > byteCeiling) {
    const next = nextQualityStep(quality, fallbacks)
    if (next === null) break
    quality = next
    dataUrl = encode(quality)
  }

  return { dataUrl, quality }
}
